import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../lib/supabaseClient";
import { playSound } from "../lib/sound.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const activityTouchedRef = useRef(false);

  const loadProfile = useCallback(async (userId) => {
    if (!userId) {
      setProfile(null);
      return;
    }
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) {
      console.error("Failed to load profile:", error.message);
      setProfile(null);
      return;
    }
    setProfile(data);
  }, []);

  // Records last_active_at and updates the daily streak, atomically, via a
  // Postgres function scoped to the calling user (see supabase/migrations/20260828220446_streaks.sql).
  // Runs once per session — after that, current_streak is just part of the
  // profile object like everything else.
  const touchActivity = useCallback(async () => {
    const { data, error } = await supabase.rpc("touch_activity");
    if (error) {
      console.error("Failed to record activity:", error.message);
      return;
    }
    if (data) {
      setProfile((prev) => {
        if (data.current_streak > (prev?.current_streak ?? 0)) {
          playSound("coin");
        }
        return data;
      });
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!isMounted) return;
      setSession(initialSession);
      loadProfile(initialSession?.user?.id).finally(() => setLoading(false));
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      loadProfile(newSession?.user?.id);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  useEffect(() => {
    if (session?.user?.id && !activityTouchedRef.current) {
      activityTouchedRef.current = true;
      touchActivity();
    }
    if (!session) {
      activityTouchedRef.current = false;
    }
  }, [session, touchActivity]);

  // Family/invite-gated sign up: an invite_codes row must exist and still
  // have uses remaining. This is how the app stays locked to family members
  // now, while the same profiles.subscription_status column supports paid
  // tiers later without a schema change.
  const signUpWithInvite = useCallback(async ({ email, password, displayName, inviteCode }) => {
    setAuthError(null);

    const normalizedCode = inviteCode.trim().toUpperCase();

    // Validated via a SECURITY DEFINER function, not a direct table select —
    // invite_codes has no select policy, so the client can only ever learn
    // the status of the ONE code it already typed in, never enumerate the
    // rest. See supabase/migrations/20260828220450_security_fixes.sql for why.
    const { data: status, error: inviteError } = await supabase.rpc("validate_invite_code", {
      p_code: normalizedCode,
    });

    if (inviteError) {
      const msg = "Something went wrong checking that invite code. Try again.";
      setAuthError(msg);
      return { error: msg };
    }

    if (status === "exhausted") {
      const msg = "That invite code has already been used up.";
      setAuthError(msg);
      return { error: msg };
    }

    if (status !== "valid") {
      const msg = "That invite code isn't recognized. Ask the arcade owner for a fresh one.";
      setAuthError(msg);
      return { error: msg };
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          display_name: displayName,
          invite_code: normalizedCode,
        },
      },
    });

    if (error) {
      setAuthError(error.message);
      return { error: error.message };
    }

    // Decrement invite uses (RLS policy restricts this to a controlled RPC in production;
    // see supabase/migrations/20260828220442_init_schema.sql for the redeem_invite_code() function).
    await supabase.rpc("redeem_invite_code", { p_code: normalizedCode });

    return { data };
  }, []);

  const signIn = useCallback(async ({ email, password }) => {
    setAuthError(null);
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      setAuthError(error.message);
      return { error: error.message };
    }
    return { data };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setProfile(null);
  }, []);

  const value = {
    session,
    user: session?.user ?? null,
    profile,
    loading,
    authError,
    signIn,
    signUpWithInvite,
    signOut,
    refreshProfile: () => loadProfile(session?.user?.id),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
